import { Formik, type FormikProps } from 'formik'
import cloneDeep from 'lodash-es/cloneDeep'
import isEqual from 'lodash-es/isEqual'
import set from 'lodash-es/set'
import React, { type ComponentProps, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SideSummary, UnavailableResource } from '@components/common'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { AlertModal, ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'
import { getPrice } from '@fourthwall/utils'
import { arrayMove } from '@fourthwall/utils/dist/array'
import { formatCurrency } from '@fourthwall/utils/dist/currency'
import { errorMessageSelector } from '@fourthwall/utils/dist/selectors'
import { pluralize } from '@fourthwall/utils/dist/string'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { useDeleteOfferMutation } from '@mutations'
import { useIsPaymentMethodModalRequiredMutation } from '@mutations/useIsPaymentMethodModalRequiredMutation'
import { reverse, routing } from '@utils/routing'
import { getTierIdsFromMap } from '@utils/tiers'

import { useAdminModeContext } from '../../../App/providers/AdminModeProvider'
import { useShopShippingFlatRatesQuery } from '../../hooks/useShopShippingFlatRatesQuery'
import { PaymentMethodRequiredModal } from '../../modules/PaymentMethods'
import { LABEL_BY_FULFILLMENT_SERVICE, LABEL_BY_MANUFACTURING_SERVICE } from '../Product.consts'
import { isShipmentStartDateEditable } from '../Product.utils'
import { ProductStatusTagDropdownContainer } from '../ProductStatusTagDropdown'
import { ProductSlugUpdateModal } from '../components/ProductSlugUpdateModal'
import { useOffer } from '../hooks/useOffer'
import { useOfferSlugSuggestionMutation } from '../hooks/useOfferSlugSuggestionMutation'
import { useUnarchiveProduct } from '../hooks/useUnarchiveProduct'
import { productValidatorSchema } from '../validators/productValidatorSchema'
import { validate } from '../validators/validate'
import { useProductBanners } from './ProductBanners/hooks/useProductBanners'
import { setVariantsCompareAtValue } from './ProductPricing/utils/setVariantsCompareAtValue'
import { setVariantsPriceValue } from './ProductPricing/utils/setVariantsPriceValue'
import { CatalogProductDetails } from './ProductSidebar/modules/CatalogProductDetails/CatalogProductDetails'
import { CatalogProductionMethod } from './ProductSidebar/modules/CatalogProductionMethod/CatalogProductionMethod'
import { OfferListingType } from './ProductSidebar/modules/OfferListingType'
import type {
  ApplyToAllVariantsProps,
  ProductFormState,
  ProductViewProps,
} from './SingleProduct.types'
import {
  convertCompareAtPriceToMoneyValueOrNull,
  convertCreatorDeclaredCostToMoneyValueOrNull,
  findFirstVariantRequiringPaymentMethod,
  getInitialValues,
  getTotalDigitalFilesSize,
  isFreeProduct,
} from './SingleProduct.utils'
import { SingleProductView } from './SingleProductView'
import { DigitalProductUpdateModal } from './components/DigitalProductUpdateModal'
import { OfferImages } from './components/OfferImages/OfferImages'
import { useAssignBulkImagesMutation } from './hooks/useAssignBulkImagesMutation'
import { useAvailabilityMutation } from './hooks/useAvailabilityMutation'
import { useManufacturerFulfillerPairs } from './hooks/useManufacturerFulfillerPairs'
import { useOfferOrdersByVariantsAnalyticsQuery } from './hooks/useOfferOrdersByVariantsAnalyticsQuery'
import { useOfferPendingOrdersQuery } from './hooks/useOfferPendingOrdersQuery'
import { useOrderSamples } from './hooks/useOrderSamples'
import { useProductPricing } from './hooks/useProductPricing'
import { useProductQuery } from './hooks/useProductQuery'
import { useShippingValidator } from './modules/shipping/useShippingValidator'
import { useVariantsStock } from './modules/variants'
import { useStatusMutation, useUpdateOfferMutation, useUpdateOfferSlugMutation } from './mutations'

type UploadProductRequestPayload = Parameters<
  ReturnType<typeof useUpdateOfferMutation>['updateOfferMutation']['mutate']
>[0]

export const SingleProduct = () => {
  const { productId = '' } = useParams<'productId'>()
  const navigate = useNavigate()
  const { open } = useModalContext()
  const adminMode = useAdminModeContext()
  const { hasPermission } = useDashboardPermissions()
  const { currentShop } = useCurrentShop()
  const {
    offer,
    isLoading: offerIsLoading,
    isSuccess: isOfferSuccess,
    isError: isOfferError,
    getOfferFromOfferData,
    setOfferData,
  } = useOffer(productId)
  const { isUpdatingStock } = useVariantsStock({ productId })
  const { updateOfferMutation } = useUpdateOfferMutation()
  const productUpdateIsSubmitting = updateOfferMutation.isLoading
  const [isUploadingImages, setIsUploadingImages] = useState(false)
  const { getSlugSuggestionMutation } = useOfferSlugSuggestionMutation()
  const { availabilityMutation } = useAvailabilityMutation()
  const { statusMutation } = useStatusMutation()
  const { shopShippingFlatRatesQuery } = useShopShippingFlatRatesQuery()
  const { productQuery } = useProductQuery([offer?.productId!], {
    enabled: !!offer?.productId,
  })

  const productBanners = useProductBanners({
    offer,
    product: productQuery.data,
  })

  const { validateManufacturerFulfillerPair } = useManufacturerFulfillerPairs()
  const { onOrderSamples } = useOrderSamples(productId)
  const formRef = useRef<FormikProps<ProductFormState>>(null)
  const { assignBulkImagesMutation } = useAssignBulkImagesMutation()
  const { offerOrdersByVariantsAnalyticsQuery } = useOfferOrdersByVariantsAnalyticsQuery([
    { offerId: productId },
  ])
  const { offerPendingOrdersQuery } = useOfferPendingOrdersQuery([{ offerId: productId }], {
    enabled: !!productId && hasPermission('products.edit.details'),
  })
  const totalUnitsSold = offerOrdersByVariantsAnalyticsQuery.data?.totalUnitsSold
  const { pricingType, setPricingType, showPricingPerVariant } = useProductPricing()
  const pendingOrdersCount = offerPendingOrdersQuery?.data?.count
  const { updateOfferSlugMutation } = useUpdateOfferSlugMutation()
  const { deleteOfferMutation } = useDeleteOfferMutation()
  const { isPaymentMethodModalRequiredMutation } = useIsPaymentMethodModalRequiredMutation()
  const { openUnarchiveProductModal } = useUnarchiveProduct()
  // validators
  const shippingValidatorSchema = useShippingValidator()

  const initialValues = getInitialValues({ offer })

  // offer type should be picked from a catalog product type
  // at first place. If there's no catalog product connected,
  // it should pick it from the selector
  const getOfferType = () => {
    return productQuery.data?.fulfillmentType || offer.offerType
  }

  // listing type should be equal to a fulfillment type
  // if an offer is connected with any catalog product
  // one manufacturer can support more than one fulfillment type
  const getListingType = () => {
    return productQuery.data?.fulfillmentType || offer.listingType
  }

  const getShipmentStartDate = (
    shipmentValue: ProductFormState['shipment'],
    manufacturingService: ProductFormState['manufacturingService'],
  ) => {
    if (!isShipmentStartDateEditable(manufacturingService)) {
      return { type: 'UNKNOWN' }
    }

    if (shipmentValue.type === 'DATE_RANGE') {
      return { type: shipmentValue.type, ...shipmentValue.dates }
    }

    if (shipmentValue.type === 'DAYS_RANGE') {
      return { type: shipmentValue.type, ...shipmentValue.days }
    }

    if (shipmentValue.type === 'UNKNOWN') {
      return { type: shipmentValue.type }
    }
  }

  const handleAvailabilityChange = (isSoldOut: boolean) => {
    availabilityMutation.mutate(
      { offerId: productId, params: { available: !isSoldOut } },
      {
        onSuccess: (data) => {
          setOfferData(data)
        },
        onError: (error) => {
          Toasts.notify(errorMessageSelector(error.response?.data), { type: 'error' })
        },
      },
    )
  }

  const handleChangeUrl: ProductViewProps['onChangeUrl'] = (slug) => {
    updateOfferSlugMutation.mutate([{ offerId: productId }, { newSlug: slug }], {
      onSuccess: (data) => {
        setOfferData(data)
      },
    })
  }

  const handleUnarchive: ComponentProps<typeof SingleProductView>['onUnarchive'] = () => {
    openUnarchiveProductModal({
      name: offer.name,
      id: productId,
      onSuccess: (data) => {
        setOfferData(data)
      },
    })
  }

  const handleNameChange = () => {
    getSlugSuggestionMutation.mutate([{ offerId: productId }], {
      onSuccess: (data) => {
        if (data.type === 'SUGGESTION') {
          open(ProductSlugUpdateModal, {
            baseUrl: `${currentShop?.baseUri!}/products/`,
            newSlug: data.slug,
            prevSlug: offer.slug,
            onConfirm: () => {
              handleChangeUrl(data.slug)
            },
          })
        }
      },
    })
  }

  const handleSubmit = async (values: ProductFormState) => {
    let updatedValues = { ...values }

    // NOTE: This is only for admins - force changing fulfillmentService before publishing product
    if (
      adminMode?.isActive &&
      getOfferType() === 'MANUAL' &&
      values.fulfillmentService === 'FULFILLED_BY_FOURTHWALL' &&
      initialValues.isSoldOut &&
      !values.isSoldOut
    ) {
      return open(AlertModal, {
        title: 'Unable to publish product',
        text: 'Make sure to change fulfiller from Fulfilled by Fourthwall before marking product as not sold out.',
      })
    }

    try {
      const hasValidManufacturerAndFulfillerPair = validateManufacturerFulfillerPair({
        manufacturer: values.manufacturingService,
        fulfiller: values.fulfillmentService,
      })

      if (adminMode?.isActive && !hasValidManufacturerAndFulfillerPair) {
        await new Promise<void>((resolve, reject) => {
          open(ConfirmModal, {
            title: 'Please confirm this manufacturer + fulfiller pair',
            text: `We do not frequently use '${
              (LABEL_BY_MANUFACTURING_SERVICE as any)[values.manufacturingService]
            }' with '${
              (LABEL_BY_FULFILLMENT_SERVICE as any)[values.fulfillmentService]
            }'. Please click save below to confirm`,
            onCancel: () => reject(new Error('Invalid manufacturer + fulfiller pair')),
            onConfirm: () => resolve(),
          })
        })
      }

      if (
        !isEqual(initialValues.digitalItems, values.digitalItems) &&
        totalUnitsSold &&
        totalUnitsSold > 0
      ) {
        await new Promise<void>((resolve, reject) => {
          open(DigitalProductUpdateModal, {
            onConfirm: (digitalItemsUpdateOrders) => {
              updatedValues = { ...updatedValues, digitalItemsUpdateOrders }
              resolve()
            },
            onCancel: () => reject(new Error('Digital items update canceled')),
          })
        })
      }

      if (
        !isEqual(initialValues.shipment, values.shipment) &&
        ['DATE_RANGE', 'DAYS_RANGE'].includes(values.shipment.type as string) &&
        values.shipmentDateUpdatePendingOrders &&
        values.shipmentDateNotifyPendingSupporters &&
        pendingOrdersCount
      ) {
        const supportersCount = `${pendingOrdersCount || 0} ${pluralize(
          pendingOrdersCount || 0,
          'supporter',
        )}`

        await new Promise<void>((resolve, reject) => {
          open(ConfirmModal, {
            title: `Are you sure you want to email ${supportersCount}?`,
            text: `Please confirm you want to notify ${supportersCount} about the updated delivery time.`,
            confirmLabel: 'Yes, continue',
            onConfirm: () => resolve(),
            onCancel: () => reject(new Error('Shipment date update canceled')),
          })
        })
      }

      if (getOfferType() !== 'DIGITAL') {
        const variantRequiringPaymentMethod = findFirstVariantRequiringPaymentMethod(values)

        if (variantRequiringPaymentMethod) {
          const title = {
            loss: 'Payment method required to sell products at a loss',
            belowMinimumProfit: 'Please add a payment method',
          }[variantRequiringPaymentMethod.reason]

          const sellingPrice = getPrice(Number(variantRequiringPaymentMethod.price?.value), {
            currency: variantRequiringPaymentMethod.price?.currency,
          })

          const subtitle = {
            loss: `Your selling price (${sellingPrice}) means you will lose money on orders of this product. Add a payment method to cover the difference between what you earn from the selling price and the cost of manufacturing and payment processing. Your payment method will only be charged if your account balance is not sufficient to cover the loss on an order.`,
            belowMinimumProfit: `Your selling price (${sellingPrice}) means you may lose money on orders after payment processing fees are accounted for. You can increase your selling price to ${getPrice(variantRequiringPaymentMethod.correctPrice.value, { currency: variantRequiringPaymentMethod.correctPrice.currency })} (a price that guarantees you’ll make a small profit) or add a payment method to cover potential losses. Your payment method will only be charged if your account balance is not sufficient to cover the loss on an order.`,
          }[variantRequiringPaymentMethod.reason]

          await new Promise<void>((resolve, reject) => {
            isPaymentMethodModalRequiredMutation.mutateAsync([], {
              onSuccess: (data) => {
                if (data.isPaymentMethodModalRequired) {
                  open(PaymentMethodRequiredModal, {
                    title,
                    subtitle,
                    onSuccess: () => resolve(),
                    onCancel: () => reject(new Error('Product price update canceled')),
                  })
                } else {
                  resolve()
                }
              },
            })
          })
        }
      }
    } catch (e) {
      return
    }

    return await updateProduct(updatedValues)
  }

  const updateProduct = async (values: ProductFormState) => {
    const shouldUpdateAvailability = initialValues.isSoldOut !== values.isSoldOut

    const images = values.offerImages.map((item: ProductFormState['offerImages'][number]) => ({
      ...item,
      tags: { tags: item.tags },
    }))

    await assignBulkImagesMutation.mutateAsync([
      { offerId: productId },
      { replaceAll: true },
      { images },
    ])

    const {
      withMembersOnly,
      membersOnlyType,
      membersOnlyTierIds,
      fulfillmentService,
      shipment,
      manufacturingService,
      bespokeProductId,
      shipmentDateNotifyPendingSupporters,
      shipmentDateUpdateReason,
      isCustomsInformationEnabled,
      isCreatorDeclaredCostEnabled,
      customsInformation,
      ...rest
    } = values

    const shipmentStartDate = getShipmentStartDate(shipment, manufacturingService)

    const productData: UploadProductRequestPayload[1] = {
      ...rest,
      variants: values.variants.map((variant) => {
        const updatedVariant = { ...variant }

        if (variant.compareAtPrice !== null) {
          const convertedPrice = convertCompareAtPriceToMoneyValueOrNull(variant.compareAtPrice)
          if (convertedPrice !== null) {
            updatedVariant.compareAtPrice = convertedPrice
          }
        }

        if (variant.creatorDeclaredCost !== null) {
          const convertedCost = convertCreatorDeclaredCostToMoneyValueOrNull(
            variant.creatorDeclaredCost,
          )
          if (convertedCost !== null) {
            updatedVariant.creatorDeclaredCost = convertedCost
          }
        }

        return updatedVariant
      }),
      requirements: {
        // @ts-expect-error - should be fixed with schema update
        allowedTiers: withMembersOnly
          ? { type: membersOnlyType, tiersIds: getTierIdsFromMap(membersOnlyTierIds) }
          : null,
      },
      digitalItems: values.digitalItems.map((item: any) => ({ name: item.name, uri: item.url })),
      fulfillingService: fulfillmentService,
      manufacturingService,
      // @ts-expect-error - should be fixed with schema update
      shipmentStartDate,
      shipmentDateNotifyPendingSupporters:
        shipmentStartDate?.type === 'UNKNOWN' ? false : shipmentDateNotifyPendingSupporters,
      shipmentDateUpdateReason:
        shipmentStartDate?.type === 'UNKNOWN' ? '' : shipmentDateUpdateReason,
      bespokeProductId,
      soundScanInfo: offer?.soundScanInfo,
      requirePhoneNumberFromBuyer: !!values.requirePhoneNumberFromBuyer,
      categoryId: values.categoryId && values.categoryId.trim() ? values.categoryId : undefined,
      customsInformation: {
        shortHsCode: isCustomsInformationEnabled
          ? values.customsInformation?.shortHsCode || undefined
          : undefined,
        countryOfOrigin:
          isCustomsInformationEnabled && !!values.customsInformation?.countryOfOrigin
            ? values.customsInformation?.countryOfOrigin
            : undefined,
      },
    }

    if (shouldUpdateAvailability) {
      handleAvailabilityChange(values.isSoldOut)
    }

    updateOfferMutation.mutate([productId, productData], {
      onSuccess: (data) => {
        setOfferData(data)
        formRef.current?.resetForm({
          values: getInitialValues({ offer: getOfferFromOfferData(data) }),
        })

        if (initialValues.name !== values.name) {
          handleNameChange()
        }

        Toasts.notify('Product has been updated successfully!', { type: 'info' })
      },
    })
  }

  if (offer.fulfillmentService === 'COMBINED' && offer.id) {
    navigate(
      `${reverse(routing.products.combined.combinedId.self, { combinedId: offer.id })}${location.search}`,
    )
  }

  if (offer.fulfillmentService === 'GIFT_CARD' && offer.id) {
    navigate(
      `${reverse(routing.products.giftCard.giftCardId.self, { giftCardId: offer.id })}${location.search}`,
    )
  }

  if (offer.fulfillmentService === 'BUNDLE' && offer.id) {
    navigate(
      `${reverse(routing.products.bundle.bundleId.self, { bundleId: offer.id })}${location.search}`,
    )
  }

  if (isOfferError) {
    return <UnavailableResource resource="product" />
  }

  if (!isOfferSuccess) {
    return null
  }

  const applyToAllVariants = ({
    priceValue,
    compareAtPriceValue,
    size,
  }: ApplyToAllVariantsProps) => {
    let newVariants = formRef.current?.values.variants || []

    if (priceValue) {
      newVariants = setVariantsPriceValue(newVariants, priceValue, size)
    }

    if (compareAtPriceValue !== undefined) {
      newVariants = setVariantsCompareAtValue(newVariants, compareAtPriceValue, size)

      if (compareAtPriceValue === null) {
        formRef.current?.setFieldValue('compareAtPriceValue', null)
      }
    }

    formRef.current?.setFieldValue('variants', newVariants)
  }

  const togglePricingType = () => {
    setPricingType((prevValue) => {
      if (prevValue === 'EXPLICIT') {
        const headVariant = formRef.current?.values.variants[0]

        if (headVariant) {
          const priceValue = formatCurrency(headVariant.price.value)
          const compareAtPriceValue = headVariant.compareAtPrice?.value
            ? formatCurrency(headVariant.compareAtPrice.value)
            : null
          formRef.current?.setFieldValue('sellingPriceValue', priceValue)
          formRef.current?.setFieldValue('compareAtPriceValue', compareAtPriceValue)
          applyToAllVariants({ priceValue, compareAtPriceValue })
        }
      }

      return prevValue === 'EXPLICIT' ? 'IMPLICIT' : 'EXPLICIT'
    })
  }

  const getMaxDigitalFilesSize = (values: ProductFormState) => {
    return isFreeProduct(values) && !values.withMembersOnly
      ? offer.config?.digitalFileProperties?.maxDigitalFileSizeForFreeOffer
      : offer.config?.digitalFileProperties?.maxDigitalFileSize
  }

  const getIsTotalDigitalFilesSizeExceeded = (values: ProductFormState) => {
    const maxSize = getMaxDigitalFilesSize(values)
    if (!maxSize) return false

    const totalSize = getTotalDigitalFilesSize(values.digitalItems)

    return totalSize > maxSize
  }

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validate={(values) =>
        validate(values)({
          productSchema: productValidatorSchema({
            manufacturingService: offer.manufacturingService,
            areFlatRatesEnabled: Boolean(shopShippingFlatRatesQuery.data?.enabled),
            isTotalDigitalFilesSizeExceeded: getIsTotalDigitalFilesSizeExceeded(values),
            isProductCategoryRequired: ['DIGITAL', 'MANUAL'].includes(getOfferType()),
          }),
          shippingSchema: shippingValidatorSchema(values),
        })
      }
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <SingleProductView
          productId={productId}
          productCustomizationId={offer.customizationId}
          productIsDeleting={deleteOfferMutation.isLoading}
          productUpdateIsSubmitting={productUpdateIsSubmitting}
          productIsFetching={offerIsLoading || updateOfferSlugMutation.isLoading}
          shippingFlatRatesAreFetching={shopShippingFlatRatesQuery.isLoading}
          productStatusIsUpdating={statusMutation.isLoading}
          productStockIsSubmitting={isUpdatingStock}
          offerImages={
            values.variantTypes ? (
              <OfferImages
                offerVariantTypes={values.variantTypes}
                value={values.offerImages}
                disabled={!hasPermission('products.edit.details')}
                onChange={(value) => {
                  setFieldValue('offerImages', value)
                }}
                onColorFilterSort={(oldIndex, newIndex) => {
                  const colorVariantTypeIndex = values.variantTypes.findIndex(
                    ({ type }: any) => type === 'COLOR',
                  )
                  const colorVariantType = values.variantTypes[colorVariantTypeIndex]

                  if (!colorVariantType) return

                  const path = `[${colorVariantTypeIndex}].options`
                  const newOptions = arrayMove(colorVariantType.options, oldIndex, newIndex)
                  const newVariantTypes = set(cloneDeep(offer.variantTypes), path, newOptions)

                  setFieldValue('variantTypes', newVariantTypes)
                }}
                onUploadStart={() => setIsUploadingImages(true)}
                onUploadEnd={() => setIsUploadingImages(false)}
              />
            ) : null
          }
          images={offer.images}
          rightContent={
            <SideSummary>
              <SideSummary.Item title="Listing Type">
                <OfferListingType product={productQuery.data} listingType={getListingType()} />
              </SideSummary.Item>
              {hasPermission('contributions.profit') && (
                <SideSummary.Item title="Analytics">
                  {totalUnitsSold ? `${totalUnitsSold} sold` : 'No sales yet'}
                </SideSummary.Item>
              )}
              {productQuery.data && (
                <>
                  <SideSummary.Item title="Product details">
                    <CatalogProductDetails product={productQuery.data} />
                  </SideSummary.Item>
                  <SideSummary.Item title="Production method">
                    <CatalogProductionMethod offerId={productId} product={productQuery.data} />
                  </SideSummary.Item>
                </>
              )}
            </SideSummary>
          }
          productBanners={productBanners}
          name={offer.name}
          status={<ProductStatusTagDropdownContainer />}
          maxDigitalFilesSize={getMaxDigitalFilesSize(values)}
          isTotalDigitalFilesSizeExceeded={getIsTotalDigitalFilesSizeExceeded(values)}
          isFreeProduct={isFreeProduct(values)}
          slug={offer.slug}
          shopBaseUri={currentShop?.baseUri}
          offerType={getOfferType()}
          isUploadingImages={isUploadingImages}
          onChangeUrl={handleChangeUrl}
          onOrderSamples={onOrderSamples}
          onUnarchive={handleUnarchive}
          pricingType={pricingType}
          showPricingPerVariant={showPricingPerVariant}
          togglePricingType={togglePricingType}
          applyToAllVariants={applyToAllVariants}
        />
      )}
    </Formik>
  )
}
